/**
 * RouteErrorPage -- root errorElement (FEAT-047 TASK-023, design.md 1.3).
 *
 * A 404 route response renders the NotFoundPage; any other failure (render, loader, chunk)
 * renders "something went wrong" with reload + go-to-start. The raw error only goes to the
 * console in development; users never see messages, stacks or status codes.
 */

import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/Button';
import { RouteStatusPage } from '@/components/routing/RouteStatusPage';
import { useHomeDestination } from '@/components/routing/useHomeDestination';
import { NotFoundPage } from './NotFoundPage';

const ICON_SIZE = 48;

function UnexpectedRouteError({ error }: { error: unknown }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const home = useHomeDestination();

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.error('[route error]', error);
    }
  }, [error]);

  const homeLabel = intl.formatMessage(
    { id: 'notFound.action.home' },
    { destination: home.key },
  );

  return (
    <RouteStatusPage
      icon={<WarningCircle size={ICON_SIZE} weight="regular" className="text-error" aria-hidden="true" />}
      title={intl.formatMessage({ id: 'routeError.title' })}
      description={intl.formatMessage({ id: 'routeError.description' })}
      homePath={home.path}
      homeLabel={homeLabel}
      actions={
        <>
          <Button variant="primary" size="lg" onClick={() => window.location.reload()}>
            {intl.formatMessage({ id: 'routeError.action.reload' })}
          </Button>
          <Button
            variant="ghost"
            size="lg"
            isLoading={home.isResolving}
            onClick={() => navigate(home.path, { replace: true })}
          >
            {homeLabel}
          </Button>
        </>
      }
    />
  );
}

export function RouteErrorPage() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }
  return <UnexpectedRouteError error={error} />;
}

export default RouteErrorPage;
